import { graphql, useStaticQuery } from 'gatsby';
import gql from 'graphql-tag';
import React, { useState } from 'react';
import { feedback } from 'react-feedbacker';
import { useMutation } from 'urql';

import {
  Alert,
  Button,
  Card,
  CardCloseButton,
  CardOptions,
  ErrorMessage,
  Form,
  TextareaField,
  useModalContext,
} from '@/components';
import { useTranslate } from '@/contexts';
import { useIsMounted } from '@/hooks';
import { Nullable } from '@/types';
import { assert } from '@/utils/error';
import {
  RemoveRewardPlayersMutation,
  RemoveRewardPlayersMutationVariables,
} from './__generated__/RewadRemovePlayersForm';

const query = graphql`
  query SanityRewardRemovePlayersForm {
    sanityRewardRemovePlayersForm {
      title {
        ...SanityLocaleString
      }
      reason {
        ...SanityLocaleString
      }
      removePlayersFromReward {
        ...SanityLocaleString
      }
      removePlayers {
        ...SanityLocaleString
      }
      playersRemoved {
        ...SanityLocaleString
      }
    }
  }
`;

const removeRewardPlayersMutation = gql`
  mutation RemoveRewardPlayers($id: String!, $reason: NonEmptyString!) {
    removeReward(id: $id, reason: $reason)
  }
`;

type Props = {
  rewardId: string;
};

type FormValues = {
  reason: string;
};

const RewardRemovePlayersForm = ({ rewardId }: Props) => {
  const staticData =
    useStaticQuery<Queries.SanityRewardRemovePlayersFormQuery>(query);

  const [errorMessage, setErrorMessage] = useState<Nullable<string>>(null);
  const form = staticData.sanityRewardRemovePlayersForm;

  assert(form, 'missing form data');
  const { t } = useTranslate();
  const isMounted = useIsMounted();
  const { close } = useModalContext();
  const [removeRewardPlayersState, removeRewardPlayers] = useMutation<
    RemoveRewardPlayersMutation,
    RemoveRewardPlayersMutationVariables
  >(removeRewardPlayersMutation);

  const defaultValues: FormValues = {
    reason: '',
  };

  const onSubmit = (values: FormValues) => {
    const reason = values.reason.trim();

    const variables = {
      id: rewardId,
      reason,
    };

    return removeRewardPlayers(variables).then((res) => {
      if (res.error?.message && isMounted) {
        setErrorMessage(res.error.message);
      } else if (close) {
        feedback.success(t(form.playersRemoved));
        close();
      }
    });
  };

  if (!form) {
    return null;
  }

  return (
    <Card
      size="lg"
      title={t(form.title)}
      options={
        <CardOptions>
          <CardCloseButton />
        </CardOptions>
      }
    >
      <Form defaultValues={defaultValues} onSubmit={onSubmit} className="grid">
        <div className="p-3 space-y-4">
          <div className="text-gray-700 dark:text-white">
            {t(form.removePlayersFromReward)}
          </div>
          <Alert variant="box">
            <span className="font-semibold">This action cannot be undone.</span>{' '}
            By removing all players, this reward will be disabled.
          </Alert>
          <TextareaField
            name="reason"
            placeholder="Reason"
            title="Reason"
            id="RewardRemovePlayersForm__reason"
            required="Reason is required in order to delete all players"
          />
          <ErrorMessage message={errorMessage} />
          <div className="flex justify-end gap-3 mt-3">
            <Button
              type="button"
              variant="secondary"
              onClick={() => close && close()}
              disabled={removeRewardPlayersState.fetching}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              className="!bg-red-600 hover:!bg-red-500 disabled:!bg-red-300"
              disabled={removeRewardPlayersState.fetching}
            >
              Delete
            </Button>
          </div>
        </div>
      </Form>
    </Card>
  );
};

export default RewardRemovePlayersForm;
